import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EuStars } from "../components/EuStars";

export default function InviteAccept() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading, refresh } = useAuth();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [joining, setJoining] = useState(false);

    useEffect(() => {
        api.get(`/team/join/${token}`)
            .then(({ data }) => setInfo(data))
            .catch((err) => {
                const d = err?.response?.data?.detail;
                setError(typeof d === "string" ? d : "This invite is invalid or has expired.");
            });
    }, [token]);

    async function join() {
        setJoining(true);
        try {
            const { data } = await api.post(`/team/join/${token}`, {});
            await refresh();
            toast.success(`Welcome to ${data.team_name || "the team"}!`);
            navigate("/app/team", { replace: true });
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not join the team");
            setJoining(false);
        }
    }

    const emailMismatch =
        user && info && user.email.toLowerCase() !== info.email.toLowerCase();

    return (
        <div className="min-h-screen bg-[#F5F0E8] text-[#0D1B3E] flex flex-col">
            <header className="container-edit py-6">
                <Link to="/" className="flex items-center gap-3" data-testid="invite-brand">
                    <EuStars size={32} />
                    <span className="font-display text-xl tracking-tight">EuroLaw Hub</span>
                </Link>
            </header>
            <main className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md card-flat p-8 bg-white/80" data-testid="invite-card">
                    {error ? (
                        <>
                            <h1 className="font-display text-2xl tracking-tight" data-testid="invite-error">
                                {error}
                            </h1>
                            <p className="mt-3 text-sm text-[#0D1B3E]/70">
                                Ask your team admin to send a fresh invite.
                            </p>
                            <Link to="/" className="btn-secondary mt-6 inline-flex">Back to home</Link>
                        </>
                    ) : !info ? (
                        <p className="text-[#0D1B3E]/60 flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin" /> Checking invite…
                        </p>
                    ) : (
                        <>
                            <Users size={28} color="#C9A84C" />
                            <div className="label-eyebrow mt-4">Team invitation</div>
                            <h1 className="font-display text-2xl lg:text-3xl mt-2 tracking-tight" data-testid="invite-team-name">
                                Join {info.team_name}
                            </h1>
                            <p className="mt-3 text-sm text-[#0D1B3E]/75">
                                {info.invited_by ? `${info.invited_by} invited` : "You have been invited as"}{" "}
                                <strong>{info.email}</strong> to a shared EuroLaw Hub workspace
                                with pooled credits and full Team features.
                            </p>

                            {authLoading ? null : user ? (
                                emailMismatch ? (
                                    <div className="mt-6 text-sm border border-amber-500/40 bg-amber-50 text-amber-900 px-3 py-2" data-testid="invite-mismatch">
                                        You are signed in as <strong>{user.email}</strong>, but this
                                        invite was issued for <strong>{info.email}</strong>. Sign out
                                        and use the invited account.
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={join}
                                        disabled={joining}
                                        className="btn-primary w-full mt-7"
                                        data-testid="invite-join-button"
                                    >
                                        {joining ? <Loader2 size={16} className="animate-spin" /> : "Join team"}
                                    </button>
                                )
                            ) : (
                                <div className="mt-7 space-y-3">
                                    <Link
                                        to={`/signup?invite=${token}&email=${encodeURIComponent(info.email)}`}
                                        className="btn-primary w-full justify-center"
                                        data-testid="invite-signup-link"
                                    >
                                        Create account & join
                                    </Link>
                                    <Link
                                        to={`/login?next=${encodeURIComponent(`/invite/${token}`)}`}
                                        className="btn-secondary w-full justify-center"
                                        data-testid="invite-login-link"
                                    >
                                        I already have an account
                                    </Link>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
